import React from 'react';
import type { CustomActionResponse } from '@troia-v3/types';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { methodBadgeStyle } from './toolStyles';

/**
 * Linha de tabela de ferramenta (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + coluna multi-linha (nome+descrição) +
 * método + endpoint + switch de status (F47) + menu ••• (última col).
 */
export interface ToolListItemProps {
  action: CustomActionResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (action: CustomActionResponse) => void;
  onViewLogs?: (action: CustomActionResponse) => void;
}

export const ToolListItem: React.FC<ToolListItemProps> = ({
  action,
  selected = false,
  onToggleSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewLogs,
}) => {
  const isActive = action.status === 'active';
  const method = action.endpoint.method;

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(action.id) });
  if (onViewLogs) actions.push({ label: 'Logs de Requisições', icon: 'file-text', onClick: () => onViewLogs(action) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'play',
      onClick: () => onToggleStatus(action),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(action.id), variant: 'danger' });

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`tool-row-${action.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(action.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`tool-check-${action.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Ferramenta: nome + descrição (mockup: SEM tile de ícone) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {action.displayName}
            </div>
            <div className="text-xs truncate max-w-[420px]" style={{ color: 'var(--theme-muted-text)' }}>
              {action.description || 'Sem descrição'}
            </div>
          </div>
        </div>
      </td>

      {/* Método */}
      <td className="px-4 py-3 w-[110px]">
        <span
          className="inline-flex items-center h-[22px] px-2 rounded-md text-[11px] font-extrabold tracking-wide"
          style={methodBadgeStyle(method)}
        >
          {method}
        </span>
      </td>

      {/* Endpoint */}
      <td className="px-4 py-3">
        <span className="text-xs font-mono truncate block max-w-[360px]" style={{ color: 'var(--theme-card-text)' }} title={action.endpoint.url}>
          {action.endpoint.url}
        </span>
      </td>

      {/* Status — F47: switch inline (Switch shared sm) + rótulo do estado */}
      <td className="px-4 py-3 w-[120px]" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`tool-toggle-${action.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(action)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`tool-status-${action.id}`}
          />
        )}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`tool-menu-${action.id}`} />
      </td>
    </tr>
  );
};

ToolListItem.displayName = 'ToolListItem';
